import { GraphQLSchema } from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import { sortBy } from 'lodash';
import * as monacoEditor from 'monaco-editor';

import { getRootTypeNames } from 'src/lib/graphql/graphqlHelpers';

import { getUnusedVariableRanges } from './graphqlHelpers';
import {
  GraphqlDefinition,
  getActiveFragment,
  getDocumentDefinitions,
  getUsedFragments,
} from './graphqlParseHelpers';

/**
 * Given a model of a document and an array of ranges, mask/replace content
 * in the given ranges with whitespace in such a way that ensures the
 * positions of the kept ranges remain the same.
 */
function maskDocument(
  model: monacoEditor.editor.ITextModel,
  maskedRanges: monacoEditor.Range[],
): string {
  return maskedRanges.reduce((result, range) => {
    return [
      result.slice(0, model.getOffsetAt(range.getStartPosition())),
      model.getValueInRange(range).replace(/[^\r\n]/g, ' '),
      result.slice(model.getOffsetAt(range.getEndPosition())),
    ].join('');
  }, model.getValue());
}

/**
 * Given a set S of overlapping or non overlapping ranges in a document,
 * get the ranges of everything not included in S.
 */
export function invertRangesInDocument(
  model: monacoEditor.editor.ITextModel,
  ranges: monacoEditor.Range[],
): monacoEditor.Range[] {
  const sortedRanges = sortBy(ranges, (range) =>
    model.getOffsetAt(range.getStartPosition()),
  );
  const modelEndPosition = model.getPositionAt(model.getValueLength());
  const beginningRange = monacoEditor.Range.fromPositions(
    model.getPositionAt(0),
    sortedRanges[0]?.getStartPosition() ?? modelEndPosition,
  );
  return [
    beginningRange,
    ...sortedRanges.map((range, i) =>
      monacoEditor.Range.fromPositions(
        range.getEndPosition(),
        sortedRanges[i + 1]?.getStartPosition() ?? modelEndPosition,
      ),
    ),
  ];
}

/**
 * Given a set S of overlapping or non overlapping ranges in a document,
 * get the ranges of everything not included in S.
 */
export function invertRangesInDocumentForDiffs(
  model: monacoEditor.editor.ITextModel,
  ranges: monacoEditor.Range[],
): monacoEditor.Range[] {
  const totalLineCount = model.getLineCount();
  return ranges.reduce((accumulator, range, index) => {
    if (index === 0 && range.startLineNumber !== 1) {
      accumulator.push(
        new monacoEditor.Range(1, 0, range.startLineNumber - 1, 0),
      );
    }
    if (index === ranges.length - 1 && range.endLineNumber !== totalLineCount) {
      accumulator.push(
        new monacoEditor.Range(range.endLineNumber + 1, 0, totalLineCount, 0),
      );
    } else {
      const nextRangeStart = ranges[index + 1]?.startLineNumber;
      if (nextRangeStart && nextRangeStart > range.endLineNumber)
        accumulator.push(
          new monacoEditor.Range(
            range.endLineNumber + 1,
            0,
            nextRangeStart - 1,
            0,
          ),
        );
    }
    return accumulator;
  }, [] as monacoEditor.Range[]);
}

/**
 * Create a range that starts and ends at the given offset
 */
export function makeRangeFromOffset(
  offset: number,
  model: monacoEditor.editor.ITextModel,
  replaceLength: number = 0,
) {
  const startPosition = model.getPositionAt(offset);
  const endPosition = model.getPositionAt(offset + replaceLength);
  return monacoEditor.Range.fromPositions(startPosition, endPosition);
}

/**
 * converts an array of jsonc-parser edit into an array of monaco edits to be
 * used with editor.executeEdits
 */
export function jsoncEditsToMonacoEdits(
  edits: jsoncParser.Edit[],
  model: monacoEditor.editor.ITextModel,
) {
  return edits.map((edit) => ({
    text: edit.content,
    range: makeRangeFromOffset(edit.offset, model, edit.length),
  }));
}

const editorDecoratorDeltas = new Map<
  monacoEditor.editor.IStandaloneCodeEditor,
  undefined | (() => void)
>();

export function applyClassNameToRanges(
  inlineClassName: string,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  ranges?: monacoEditor.Range[],
) {
  const model = editor.getModel();
  if (model && ranges) {
    editorDecoratorDeltas.get(editor)?.();
    const decorations = editor.createDecorationsCollection(
      ranges.map((range) => ({
        range,
        options: { inlineClassName },
      })),
    );

    // Clean up decorations
    return () => {
      decorations.clear();
    };
  }
}

export function initializeGraphqlDeemphasizedRanges(
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  schema: GraphQLSchema,
) {
  const model = editor.getModel();
  if (!model) return;

  const { fragmentDefinitions, operationDefinitions, commentRanges } =
    getDocumentDefinitions(editor.getValue(), getRootTypeNames(schema));

  const inactiveRanges = getGraphqlInactiveRanges({
    model,
    fragmentDefinitions,
    activeOperation: getActiveOperation(
      editor.getPosition() ?? undefined,
      model,
      fragmentDefinitions,
      operationDefinitions,
      commentRanges,
      schema,
    ),
    activeFragment: getActiveFragment(
      editor.getPosition() ?? undefined,
      fragmentDefinitions,
    ),
  });

  editorDecoratorDeltas.set(
    editor,
    applyClassNameToRanges('opacity-50', editor, inactiveRanges),
  );
}

export function getGraphqlInactiveRanges({
  model,
  activeOperation,
  activeFragment,
  fragmentDefinitions,
}: {
  model?: monacoEditor.editor.ITextModel | null;
  fragmentDefinitions?: GraphqlDefinition[];
  activeOperation: ReturnType<typeof getActiveOperation>;
  activeFragment: ReturnType<typeof getActiveFragment>;
}) {
  // If we have inactive ranges, set their opacity to 50%
  if (activeOperation?.operationInactiveRanges) {
    // If theres a current opration, use its inactive ranges
    return activeOperation?.operationInactiveRanges;
  }

  if (model && !model.isDisposed()) {
    if (activeFragment) {
      // check if a fragment is active and get it's inactive ranges
      return invertRangesInDocument(model, [
        activeFragment.range,
        ...getUsedFragments(
          model,
          activeFragment,
          fragmentDefinitions ?? [],
        ).map((fragment) => fragment.range),
      ]);
    }
    // No definitions are active, return full range as inactive
    return [model.getFullModelRange()];
  }
}

export function getActiveOperation(
  editorPosition: monacoEditor.Position = new monacoEditor.Position(1, 1),
  model: monacoEditor.editor.ITextModel | undefined | null,
  fragmentDefinitions: GraphqlDefinition[],
  operationDefinitions: GraphqlDefinition[],
  commentRanges: monacoEditor.Range[],
  schema: GraphQLSchema,
) {
  // Get the selected (or only) operation definition
  const selectedOperation =
    operationDefinitions.length === 1
      ? operationDefinitions[0]
      : editorPosition &&
        operationDefinitions.find((definition) =>
          definition.range.containsPosition(editorPosition),
        );

  if (!selectedOperation || !model || model.isDisposed()) {
    return;
  }

  const usedFragments = getUsedFragments(
    model,
    selectedOperation,
    fragmentDefinitions,
  );
  const activeRanges = [
    selectedOperation.range,
    ...usedFragments.map((fragment) => fragment.range),
    ...commentRanges,
  ];
  const inactiveDefinitionRanges = invertRangesInDocument(model, activeRanges);
  const activePartsOfDocumentWithUnusedVariables = maskDocument(
    model,
    inactiveDefinitionRanges,
  );
  const operationInactiveRanges = [
    ...inactiveDefinitionRanges,
    ...getUnusedVariableRanges(
      activePartsOfDocumentWithUnusedVariables,
      schema,
    ),
  ];
  const activePartsOfDocument = maskDocument(model, operationInactiveRanges);
  return {
    name: selectedOperation.name,
    activeRanges,
    activePartsOfDocument,
    activePartsOfDocumentWithUnusedVariables,
    operationActiveRanges: activeRanges,
    operationInactiveRanges,
    isSubscription: selectedOperation.isSubscription,
  };
}

export const createAutocompleteSuggestion = (
  model: monacoEditor.editor.ITextModel,
  position: monacoEditor.Position,
  options: (string | { label: string; insertText: string })[],
) => {
  const wordStart = model.getWordUntilPosition(position);
  return {
    suggestions: options.map((insertText) => ({
      label: typeof insertText === 'string' ? insertText : insertText.label,
      kind: monacoEditor.languages.CompletionItemKind.Property,
      insertText:
        typeof insertText === 'string' ? insertText : insertText.insertText,
      range: {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: wordStart.startColumn,
        endColumn: wordStart.endColumn,
      },
    })),
  };
};

export const exclusiveContainsPosition = (
  range: monacoEditor.Range,
  position: monacoEditor.Position,
) => {
  return (
    range.containsPosition(position) &&
    !range.getEndPosition().equals(position) &&
    !range.getStartPosition().equals(position)
  );
};

/**
 * Doesn't currently account for editable editors, only read only.
 * Doesn't currently account for the ability of the user to fold lines.
 *
 * Get the max height in pixels between the modified editor and the original
 */
export const getMaxDiffEditorHeight = (
  editor: monacoEditor.editor.IDiffEditor,
) => {
  const modifiedHeight = editor.getModifiedEditor().getContentHeight();
  const originalHeight = editor.getModifiedEditor().getContentHeight();
  return Math.max(modifiedHeight, originalHeight);
};
